import { useReducer, useCallback, useRef, useEffect } from "react"
import dayjs from "dayjs"
import { calculateMinWidth } from "src/utils/table-render-functions"

const initialState = {
    loading: false,
    rows: [],
    table: [],
    alert: { open: false, message: "", type: "" },
}

function reducer(state, action) {
    switch (action.type) {
        case "SET_LOADING":
            return { ...state, loading: action.payload }
        case "SET_ROWS":
            return { ...state, rows: action.payload }
        case "SET_TABLE":
            return { ...state, table: action.payload }
        case "SET_ALERT":
            return { ...state, alert: { ...state.alert, ...action.payload } }
        case "CLOSE_ALERT":
            return { ...state, alert: { ...state.alert, open: false } }
        default:
            return state
    }
}

const ordenaPorDataReferencia = (arr, order = "asc") => {
    return [...arr].sort((a, b) => {
        const dateA = a?.DataReferencia
            ? new Date(a.DataReferencia)
            : new Date(0)
        const dateB = b?.DataReferencia
            ? new Date(b.DataReferencia)
            : new Date(0)
        return order === "desc" ? dateB - dateA : dateA - dateB
    })
}

export const useIndiceLiquidezTable = (
    emissaoService,
    cetipIsin,
    dadosEmpresa
) => {
    const [state, dispatch] = useReducer(reducer, initialState)

    const loadedRef = useRef(cetipIsin)
    
    useEffect(() => {
        if (loadedRef.current === cetipIsin) return

        loadedRef.current = cetipIsin
    
        dispatch({ type: "SET_ROWS", payload: [] })
        dispatch({ type: "SET_TABLE", payload: [] })
        dispatch({ type: "CLOSE_ALERT" })
            
    }, [cetipIsin])

    const buildTable = useCallback((dadosCabecalho) => {
        const { cetip, isin } = dadosCabecalho || {}

        dispatch({
            type: "SET_TABLE",
            payload: [
                {
                    cells: [
                        `CETIP: ${cetip || "N/A"}`,
                        `ISIN: ${isin || "N/A"}`,
                    ],
                },
            ],
        })
    }, [])

    const loadTableRows = useCallback(async () => {
        if (!cetipIsin || cetipIsin.length <= 4) {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "CETIP não informado ou inválido.",
                    type: "warning",
                },
            })
            // Fallback usando os dados locais
            buildTable({
                cetip: dadosEmpresa?.CETIP || cetipIsin,
                isin: dadosEmpresa?.ISIN
            })
            dispatch({ type: "SET_ROWS", payload: [] })
            return
        }

        dispatch({ type: "SET_LOADING", payload: true })
        
        try {
            const response = await emissaoService.indiceLiquidez(
                "get",
                `?CETIP=${cetipIsin.toUpperCase()}`
            )
            
            // Extração segura do array de resultados
            const listaResultados = response?.body?.results || (Array.isArray(response?.body) ? response.body : [])
            
            if (!listaResultados || listaResultados.length === 0) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Não há dados para o CETIP consultado.",
                        type: "warning",
                    },
                })
                dispatch({ type: "SET_ROWS", payload: [] })

                // Monta tabela com fallback usando os dados locais
                buildTable({
                    cetip: cetipIsin || dadosEmpresa?.CETIP,
                    isin: dadosEmpresa?.ISIN
                })
            } else {
                dispatch({ type: "CLOSE_ALERT" })
                
                const rows = ordenaPorDataReferencia(listaResultados, "desc").map(
                    (obj, i) => ({ ...obj, id: i })
                )
                
                const primeiraLinha = rows[0] || {}

                // Monta a tabela priorizando os dados da API
                buildTable({
                    cetip: primeiraLinha.CETIP || primeiraLinha.cetip || cetipIsin || dadosEmpresa?.CETIP,
                    isin: primeiraLinha.ISIN || primeiraLinha.Isin || primeiraLinha.isin || dadosEmpresa?.ISIN
                })

                dispatch({ type: "SET_ROWS", payload: rows })
            }
        } catch {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "Erro ao buscar dados ou CETIP não encontrado.",
                    type: "warning",
                },
            })
            dispatch({ type: "SET_ROWS", payload: [] })
            
            // Mantém os cabeçalhos visíveis mesmo em caso de erro
            buildTable({
                cetip: cetipIsin || dadosEmpresa?.CETIP,
                isin: dadosEmpresa?.ISIN
            })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [emissaoService, cetipIsin, buildTable, dadosEmpresa])

    const addRow = useCallback(async () => {
        let lastItem = state.rows[0]
        
        if (!lastItem) {
            lastItem = {
                CETIP: cetipIsin,
                DataReferencia: dayjs().format("YYYY-MM-DD"),
                IndiceLiquidez: 0,
            }
        }

        dispatch({ type: "SET_LOADING", payload: true })
        try {
            await emissaoService.indiceLiquidez("post", lastItem)
            await loadTableRows()
        } catch {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "Erro ao adicionar novo registro.",
                    type: "error",
                },
            })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [state.rows, cetipIsin, emissaoService, loadTableRows])

    const deleteRow = useCallback(
        async (row) => {
            if (!row?.ID) return
            dispatch({ type: "SET_LOADING", payload: true })
            try {
                await emissaoService.indiceLiquidez("delete", row.ID)
                await loadTableRows()
            } catch {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Erro ao deletar registro.",
                        type: "error",
                    },
                })
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
        },
        [emissaoService, loadTableRows]
    )

    const updateRow = useCallback(
        async (row) => {
            if (!row?.ID) return
            dispatch({ type: "SET_LOADING", payload: true })
            try {
                await emissaoService.indiceLiquidez("patch", row.ID, row)
                const updatedRows = state.rows.map((r) =>
                    r.ID === row.ID ? { ...r, ...row } : r
                )
                dispatch({ type: "SET_ROWS", payload: updatedRows })
            } catch {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Erro ao atualizar registro.",
                        type: "error",
                    },
                })
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
            return row
        },
        [state.rows, emissaoService]
    )

    const setAlert = useCallback(
        (payload) => dispatch({ type: "SET_ALERT", payload }),
        []
    )
    const closeAlert = useCallback(() => dispatch({ type: "CLOSE_ALERT" }), [])

    return {
        state: {
            ...state,
            minWidthIndiceLiquidez: calculateMinWidth(
                state.rows,
                "IndiceLiquidez"
            ),
        },
        actions: {
            loadTableRows,
            addRow,
            deleteRow,
            updateRow,
            setAlert,
            closeAlert,
            setRows: (rows) => dispatch({ type: "SET_ROWS", payload: rows }),
        },
    }
}